import React from 'react';
import clsx from 'clsx';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthSessionStore } from '@/presentation/auth/useAuthSessionStore';
import { useUserWorkspaceStore } from '@/presentation/workspace/useUserWorkspaceStore';
import { WorkspaceApiClient } from '@/infrastructure/workspace/WorkspaceApiClient';
import QuizAudioPrefsPanel from '@/presentation/quiz/QuizAudioPrefsPanel';
import { useDashboardLocaleStore } from '@/presentation/dashboard/useDashboardLocaleStore';
import { LocaleSelect } from '@/presentation/dashboard/LocaleSelect';
import { getDashboardShellStrings } from '@/presentation/dashboard/dashboardShellStrings';
import { getGuestBannerStrings } from '@/presentation/dashboard/dashboardHomeLocale';
import { useAppTranslation } from '@/i18n/useAppTranslation';
import { formatApiErrorMessage } from '@/utils/formatApiErrorMessage';

/** Mục không có `path` = tính năng chưa có trang (hiển thị mờ, không điều hướng). */
function isPlannedMenuItem(item) {
  return !item?.path;
}

function isMenuItemActive(item, pathname) {
  return Boolean(item.path && pathname.startsWith(item.path));
}

function dashboardNavItemClasses(item, pathname, variant) {
  const pad = variant === 'mobile' ? 'px-4 py-3' : 'px-6 py-3';
  const base = clsx(
    'mx-2 flex w-[calc(100%-1rem)] items-center gap-3 rounded-xl text-left transition',
    pad,
  );
  if (isPlannedMenuItem(item)) {
    return clsx(
      base,
      'pointer-events-none cursor-not-allowed opacity-45 saturate-50',
      'font-normal text-slate-500 dark:text-slate-500',
    );
  }
  if (isMenuItemActive(item, pathname)) {
    return clsx(
      base,
      'bg-cyan-100 font-bold text-cyan-900 shadow-sm ring-2 ring-cyan-300/70 ring-offset-1 ring-offset-slate-50',
      'dark:bg-cyan-950/50 dark:text-cyan-50 dark:ring-cyan-700/60 dark:ring-offset-slate-900',
    );
  }
  return clsx(
    base,
    'font-semibold text-slate-800 hover:bg-slate-200/60 hover:text-cyan-700',
    'dark:text-slate-100 dark:hover:bg-slate-800 dark:hover:text-cyan-300',
  );
}

function dashboardHomeNavClasses(isActive, variant) {
  const pad = variant === 'mobile' ? 'px-4 py-3' : 'px-6 py-3';
  const base = clsx(
    'mx-2 flex w-[calc(100%-1rem)] items-center gap-3 rounded-xl text-left transition',
    pad,
  );
  if (isActive) {
    return clsx(
      base,
      'bg-cyan-100 font-bold text-cyan-900 shadow-sm ring-2 ring-cyan-300/70 ring-offset-1 ring-offset-slate-50',
      'dark:bg-cyan-950/50 dark:text-cyan-50 dark:ring-cyan-700/60 dark:ring-offset-slate-900',
    );
  }
  return clsx(
    base,
    'font-semibold text-slate-800 hover:bg-slate-200/60 hover:text-cyan-700',
    'dark:text-slate-100 dark:hover:bg-slate-800 dark:hover:text-cyan-300',
  );
}

function MobileNavDrawer({
  open,
  onClose,
  appTitle,
  systemTagline,
  menuItems,
  roleLabel,
  onLogout,
  onOpenSupport,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const locale = useDashboardLocaleStore((s) => s.locale);
  const shell = React.useMemo(() => getDashboardShellStrings(locale), [locale]);
  const isHomeActive = location.pathname === '/dashboard';
  const go = (path) => {
    if (path) navigate(path);
    onClose();
  };
  const goIfPath = (path) => {
    if (path) go(path);
  };
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] md:hidden" role="dialog" aria-modal="true" aria-label={shell.navDialogLabel}>
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/50"
        aria-label={shell.closeOverlay}
        onClick={onClose}
      />
      <aside className="absolute left-0 top-0 flex h-full min-h-0 w-[min(20rem,92vw)] flex-col overflow-hidden border-r border-slate-200 bg-slate-50 py-4 shadow-2xl dark:border-slate-700 dark:bg-slate-900">
        <div className="mb-6 flex items-center justify-between gap-2 px-4">
          <button
            type="button"
            onClick={() => go('/dashboard')}
            className="flex items-center gap-3 rounded-xl px-1 py-1 text-left"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-100">
              <span className="material-symbols-outlined text-cyan-600">school</span>
            </div>
            <div>
              <h2 className="font-headline text-lg font-extrabold text-cyan-700">{appTitle}</h2>
              <p className="text-[10px] uppercase tracking-wider text-slate-500">{systemTagline}</p>
            </div>
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-800"
            aria-label={shell.closeMenu}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <nav className="flex-1 min-h-0 space-y-1 overflow-y-auto overscroll-contain px-2 pb-2">
          <button
            type="button"
            onClick={() => go('/dashboard')}
            className={dashboardHomeNavClasses(isHomeActive, 'mobile')}
          >
            <span className="material-symbols-outlined">dashboard</span>
            <span>{shell.home}</span>
          </button>
          {menuItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => goIfPath(item.path)}
              aria-disabled={isPlannedMenuItem(item)}
              tabIndex={isPlannedMenuItem(item) ? -1 : 0}
              className={dashboardNavItemClasses(item, location.pathname, 'mobile')}
            >
              <span className="material-symbols-outlined shrink-0">{item.icon}</span>
              <span className="min-w-0 text-left">{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="mt-auto border-t border-slate-200 px-4 pt-4 dark:border-slate-700">
          <div className="mb-3 rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            {roleLabel}
          </div>
          <button
            type="button"
            onClick={() => {
              onOpenSupport();
              onClose();
            }}
            className="flex w-full items-center gap-3 rounded-lg px-4 py-2 text-sm text-slate-500 hover:text-cyan-600 dark:text-slate-300"
          >
            <span className="material-symbols-outlined text-lg">help</span>
            {shell.support}
          </button>
          <button
            type="button"
            onClick={() => {
              onLogout();
              onClose();
            }}
            className="flex w-full items-center gap-3 rounded-lg px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30"
          >
            <span className="material-symbols-outlined text-lg">logout</span>
            {shell.logout}
          </button>
        </div>
      </aside>
    </div>
  );
}

function Sidebar({ appTitle, systemTagline, menuItems, roleLabel, onLogout, onOpenSupport }) {
  const navigate = useNavigate();
  const location = useLocation();
  const locale = useDashboardLocaleStore((s) => s.locale);
  const shell = React.useMemo(() => getDashboardShellStrings(locale), [locale]);
  const isHomeActive = location.pathname === '/dashboard';
  return (
    <aside className="fixed left-0 top-0 z-40 hidden h-screen w-64 flex-col overflow-hidden border-r border-slate-200 bg-slate-50 py-4 md:flex dark:border-slate-700 dark:bg-slate-900">
      <button
        type="button"
        onClick={() => navigate('/dashboard')}
        className="mb-8 flex items-center gap-3 px-6 pt-2 text-left"
      >
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-100">
          <span className="material-symbols-outlined text-cyan-600">school</span>
        </div>
        <div>
          <h2 className="font-headline text-lg font-extrabold text-cyan-700">{appTitle}</h2>
          <p className="text-[10px] uppercase tracking-wider text-slate-500">{systemTagline}</p>
        </div>
      </button>
      <nav className="flex min-h-0 flex-1 flex-col space-y-1 overflow-y-auto overscroll-contain px-0 pb-2">
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className={dashboardHomeNavClasses(isHomeActive, 'desktop')}
        >
          <span className="material-symbols-outlined shrink-0">dashboard</span>
          <span className="min-w-0 text-left">{shell.home}</span>
        </button>
        {menuItems.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => {
              if (item.path) navigate(item.path);
            }}
            aria-disabled={isPlannedMenuItem(item)}
            tabIndex={isPlannedMenuItem(item) ? -1 : 0}
            className={dashboardNavItemClasses(item, location.pathname, 'desktop')}
          >
            <span className="material-symbols-outlined shrink-0">{item.icon}</span>
            <span className="min-w-0 text-left">{item.label}</span>
          </button>
        ))}
      </nav>
      <div className="mx-4 mt-auto border-t border-slate-200 pt-4 dark:border-slate-700">
        <div className="mb-3 rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          {roleLabel}
        </div>
        <button type="button" onClick={onOpenSupport} className="flex w-full items-center gap-3 rounded-lg px-4 py-2 text-sm text-slate-500 hover:text-cyan-600 dark:text-slate-300">
          <span className="material-symbols-outlined text-lg">help</span>
          {shell.support}
        </button>
        <button type="button" onClick={onLogout} className="flex w-full items-center gap-3 rounded-lg px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30">
          <span className="material-symbols-outlined text-lg">logout</span>
          {shell.logout}
        </button>
      </div>
    </aside>
  );
}

function SupportModal({ open, setOpen, onSubmit }) {
  const { t } = useAppTranslation();
  const [subject, setSubject] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [result, setResult] = React.useState('');
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl dark:bg-slate-900">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-headline text-xl font-bold dark:text-slate-100">{t('support.modalTitle')}</h3>
          <button type="button" onClick={() => setOpen(false)} className="rounded-full p-1 hover:bg-slate-100 dark:hover:bg-slate-800">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <div className="mb-4 rounded-xl bg-sky-50 p-3 text-sm text-slate-700 dark:bg-slate-800 dark:text-slate-200">
          {t('support.contact')}
        </div>
        <div className="space-y-3">
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={t('support.subjectPh')}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800"
          />
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t('support.messagePh')}
            className="h-32 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800"
          />
          <button
            type="button"
            onClick={async () => {
              try {
                const ticket = await onSubmit({
                  category: 'support',
                  subject: subject || t('support.defaultSubject'),
                  message: message || t('support.defaultMessage'),
                  priority: 'normal',
                });
                setResult(t('support.created', { id: ticket.id }));
                setSubject('');
                setMessage('');
              } catch (e) {
                setResult(formatApiErrorMessage(e?.message, t) || t('support.failed'));
              }
            }}
            className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-bold text-white"
          >
            {t('support.submit')}
          </button>
          {result ? <p className="text-sm font-semibold text-emerald-700">{result}</p> : null}
        </div>
      </div>
    </div>
  );
}

function MiniPopover({ title, children, onOpenPage, buttonLabel, expandLabel, className }) {
  return (
    <div
      className={clsx(
        'absolute right-0 top-12 z-50 w-80 rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl dark:border-slate-700 dark:bg-slate-900',
        className,
      )}
    >
      <div className="mb-3 flex items-center justify-between">
        <h4 className="font-headline text-lg font-bold dark:text-slate-100">{title}</h4>
        {onOpenPage ? (
          <button type="button" onClick={onOpenPage} className="text-sm font-bold text-cyan-600 hover:underline">
            {expandLabel}
          </button>
        ) : null}
      </div>
      {children}
      {onOpenPage ? (
        <button type="button" onClick={onOpenPage} className="mt-4 w-full rounded-lg bg-cyan-600 px-3 py-2 text-sm font-bold text-white">
          {buttonLabel}
        </button>
      ) : null}
    </div>
  );
}

function Topbar({
  profile,
  notifications,
  theme,
  onOpenAccountPage,
  onOpenSettingsPage,
  onOpenNotificationsPage,
  onOpenSupport,
  onOpenMobileNav,
}) {
  const location = useLocation();
  const { t } = useAppTranslation();
  const locale = useDashboardLocaleStore((s) => s.locale);
  const setLocale = useDashboardLocaleStore((s) => s.setLocale);
  const shell = React.useMemo(() => getDashboardShellStrings(locale), [locale]);
  const [open, setOpen] = React.useState('');
  const popoverAreaRef = React.useRef(null);
  const unread = notifications.filter((n) => !n.read).length;

  React.useEffect(() => {
    setOpen('');
  }, [location.pathname]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!popoverAreaRef.current?.contains(event.target)) {
        setOpen('');
      }
    };
    const onEscape = (event) => {
      if (event.key === 'Escape') setOpen('');
    };
    window.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('keydown', onEscape);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  return (
    <header className="fixed top-0 z-30 flex h-16 w-full items-center gap-3 border-b border-slate-100 bg-white/80 px-4 backdrop-blur dark:border-slate-700 dark:bg-slate-900/80 md:left-64 md:w-[calc(100%-16rem)] md:px-6">
      <button
        type="button"
        onClick={onOpenMobileNav}
        className="shrink-0 rounded-lg p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 md:hidden"
        aria-label={shell.openMenu}
      >
        <span className="material-symbols-outlined text-2xl">menu</span>
      </button>
      <div className="hidden min-w-0 flex-1 items-center rounded-full bg-slate-100 px-4 py-2 md:flex dark:bg-slate-800">
        <span className="material-symbols-outlined mr-2 text-slate-500">search</span>
        <input
          className="w-72 border-none bg-transparent text-sm outline-none placeholder:text-slate-400 dark:text-slate-100"
          placeholder={shell.searchPlaceholder}
          type="text"
        />
      </div>
      <div ref={popoverAreaRef} className="relative ml-auto flex shrink-0 items-center gap-2 md:gap-3">
        <button
          type="button"
          onClick={() => setOpen(open === 'noti' ? '' : 'noti')}
          className="relative rounded-full p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
          aria-label={shell.notifications}
        >
          <span className="material-symbols-outlined">notifications</span>
          {unread > 0 ? <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-rose-500" /> : null}
        </button>
        {open === 'noti' ? (
          <MiniPopover
            title={shell.notifications}
            expandLabel={shell.expand}
            onOpenPage={() => {
              setOpen('');
              onOpenNotificationsPage();
            }}
            buttonLabel={shell.openNotificationsPage}
          >
            <div className="space-y-2 text-sm">
              {notifications.slice(0, 3).map((n) => (
                <div key={n.id} className="rounded-xl border border-slate-100 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-300">
                    {String(n.type || '').toLowerCase() === 'important'
                      ? t('notifications.typeImportant')
                      : String(n.type || '').toLowerCase() === 'reminder'
                        ? t('notifications.typeReminder')
                        : t('notifications.typeGeneral')}
                  </p>
                  <p className="font-semibold">
                    {String(n.title || '')
                      .trim()
                      .toLowerCase()
                      .match(/^(thông báo hệ thống|thong bao he thong|system notification)$/i)
                      ? t('notifications.systemTitle')
                      : String(n.title || '')
                          .trim()
                          .toLowerCase()
                          .match(/^(nhắc nhở|nhac nho|reminder)$/i)
                        ? t('notifications.reminderTitle')
                        : n.title}
                  </p>
                  <p className="mt-1">{n.message}</p>
                </div>
              ))}
              {notifications.length === 0 ? <p className="text-slate-500">{shell.noNotifications}</p> : null}
            </div>
          </MiniPopover>
        ) : null}

        <LocaleSelect value={locale} onChange={setLocale} ariaLabel={shell.langSwitch} />

        <button
          type="button"
          onClick={() => setOpen(open === 'settings' ? '' : 'settings')}
          className="rounded-full p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
          aria-label={shell.settings}
        >
          <span className="material-symbols-outlined">settings</span>
        </button>
        {open === 'settings' ? (
          <MiniPopover
            title={shell.settings}
            expandLabel={shell.expand}
            onOpenPage={() => {
              setOpen('');
              onOpenSettingsPage();
            }}
            buttonLabel={shell.openFullSettings}
            className="w-[min(22rem,calc(100vw-2rem))] max-h-[min(85vh,32rem)] overflow-y-auto"
          >
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {shell.themeLine}{' '}
              <strong>{theme === 'dark' ? shell.themeDark : shell.themeLight}</strong>
            </p>
            <div className="mt-3 border-t border-slate-200 pt-3 dark:border-slate-700">
              <QuizAudioPrefsPanel variant="compact" />
            </div>
          </MiniPopover>
        ) : null}

        <button
          type="button"
          onClick={() => setOpen(open === 'account' ? '' : 'account')}
          className="flex max-w-[11rem] items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 dark:border-slate-700 dark:bg-slate-800"
          aria-label={shell.account}
        >
          <div className="h-7 w-7 shrink-0 rounded-full bg-cyan-100" />
          <span className="truncate text-sm font-semibold text-slate-700 dark:text-slate-100">
            {profile?.name || shell.accountFallback}
          </span>
        </button>
        {open === 'account' ? (
          <MiniPopover
            title={shell.account}
            expandLabel={shell.expand}
            onOpenPage={
              onOpenAccountPage
                ? () => {
                    setOpen('');
                    onOpenAccountPage();
                  }
                : undefined
            }
            buttonLabel={shell.openAccountPage}
          >
            <div className="space-y-1 text-sm">
              <p>
                <strong>{shell.fieldId}:</strong> {profile?.id || 'N/A'}
              </p>
              <p>
                <strong>{shell.fieldName}:</strong> {profile?.name || 'N/A'}
              </p>
              <p>
                <strong>{shell.fieldEmail}:</strong> {profile?.email || 'N/A'}
              </p>
            </div>
          </MiniPopover>
        ) : null}

        <button type="button" onClick={onOpenSupport} className="rounded-full bg-cyan-600 p-2 text-white shadow-lg md:hidden">
          <span className="material-symbols-outlined">support_agent</span>
        </button>
      </div>
    </header>
  );
}

function GuestUpgradeBanner() {
  const navigate = useNavigate();
  const clearSession = useAuthSessionStore((s) => s.clearSession);
  const locale = useDashboardLocaleStore((s) => s.locale);
  const g = React.useMemo(() => getGuestBannerStrings(locale), [locale]);

  return (
    <section className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-bold">{g.title}</p>
          <p className="text-xs">{g.body}</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              clearSession();
              navigate('/register');
            }}
            className="rounded-lg bg-amber-600 px-3 py-2 text-xs font-bold text-white hover:bg-amber-700"
          >
            {g.register}
          </button>
          <button
            type="button"
            onClick={() => {
              clearSession();
              navigate('/login');
            }}
            className="rounded-lg border border-amber-400 px-3 py-2 text-xs font-bold text-amber-700 hover:bg-amber-100"
          >
            {g.login}
          </button>
        </div>
      </div>
    </section>
  );
}

export default function RoleDashboardShell({ session, appTitle, systemTagline, roleLabel, menuItems, onLogout, children }) {
  const theme = useUserWorkspaceStore((s) => s.theme);
  const setTheme = useUserWorkspaceStore((s) => s.setTheme);
  const api = React.useMemo(() => new WorkspaceApiClient(), []);
  const token = session?.accessToken || null;
  const [profile, setProfile] = React.useState({
    id: session?.user?.id || '',
    name: session?.user?.name || 'Tài khoản',
    email: session?.user?.email || '',
  });
  const [notifications, setNotifications] = React.useState([]);
  const [supportOpen, setSupportOpen] = React.useState(false);
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const navigate = useNavigate();

  const loadSummary = React.useCallback(async () => {
    if (!token) return;
    try {
      const [account, noti, settings] = await Promise.all([
        api.getAccount(token),
        api.getNotifications(token),
        api.getSettings(token),
      ]);
      setProfile({
        id: account?.id || session?.user?.id || '',
        name: account?.name || session?.user?.name || 'Tài khoản',
        email: account?.email || session?.user?.email || '',
      });
      setNotifications(noti?.items || []);
      if (settings?.theme === 'dark' || settings?.theme === 'light') {
        setTheme(settings.theme);
      }
    } catch {
      // keep graceful fallback for preview mode
    }
  }, [api, token, session?.user?.id, session?.user?.name, session?.user?.email, setTheme]);

  React.useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  return (
    <div className="min-h-screen bg-sky-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <Sidebar appTitle={appTitle} systemTagline={systemTagline} menuItems={menuItems} roleLabel={roleLabel} onLogout={onLogout} onOpenSupport={() => setSupportOpen(true)} />
      <MobileNavDrawer
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        appTitle={appTitle}
        systemTagline={systemTagline}
        menuItems={menuItems}
        roleLabel={roleLabel}
        onLogout={onLogout}
        onOpenSupport={() => setSupportOpen(true)}
      />
      <Topbar
        profile={profile}
        notifications={notifications}
        theme={theme}
        onOpenAccountPage={session?.accessToken ? () => navigate('/account') : null}
        onOpenSettingsPage={() => navigate('/settings')}
        onOpenNotificationsPage={() => navigate('/notifications')}
        onOpenSupport={() => setSupportOpen(true)}
        onOpenMobileNav={() => setMobileNavOpen(true)}
      />
      <main className="px-4 pb-10 pt-20 md:ml-64 md:px-8">
        {session?.type === 'guest' ? <GuestUpgradeBanner /> : null}
        {children}
      </main>
      <SupportModal
        open={supportOpen}
        setOpen={setSupportOpen}
        onSubmit={async (payload) => {
          if (!token) return { id: 'LOCAL' };
          return api.createSupportTicket(payload, token);
        }}
      />
    </div>
  );
}

